<template>
  <!-- One category banner, in whichever of the three shapes the API sent. See
       App\Models\Category::getBannerAttribute on the API side: `type` is
       "image" or "html", and exactly one of `url` / `html` is populated. -->
  <div v-if="hasBanner" class="w-full">
    <!-- An uploaded picture. Its height follows the artwork, so it needs no
         guess about the ratio and stays sharp at any column width. -->
    <img
      v-if="isImage"
      :src="banner.url"
      :alt="categoryName"
      class="block w-full h-auto rounded-[4px]"
      loading="lazy"
    />

    <!-- A document on the images host. It is cross origin - the host sends no
         CORS header and contentDocument is blocked - so its height cannot be
         measured from here and the strip has to impose one. The frame is given
         more height than a banner document needs and is then centred inside a
         shorter window, so what shows is the middle of the document rather
         than its top. That is what keeps the artwork centred and snug at
         narrow widths, where these documents scale their artwork down but
         keep their outer padding at full size. -->
    <div v-else-if="isFramedHtml" class="category-banner-window">
      <iframe
        :src="banner.url"
        :title="categoryName"
        class="category-banner-frame"
        loading="lazy"
        scrolling="no"
      ></iframe>
    </div>

    <!-- Markup pasted into KT. Same origin as the page, so it is inserted
         rather than framed and can size itself to its own content. Authored
         in the admin by staff, never by customers. -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-else v-html="banner.html"></div>
  </div>
</template>

<script>
export default {
  name: "category-banner",
  props: {
    // { type: "image" | "html", url: String|null, html: String|null }
    banner: {
      type: Object,
      default: null,
    },
    // Becomes the alt text / frame title, so the banner is not anonymous to
    // a screen reader.
    categoryName: {
      type: String,
      default: "",
    },
  },
  computed: {
    isImage() {
      return this.banner?.type === "image" && Boolean(this.banner.url);
    },
    isFramedHtml() {
      return this.banner?.type === "html" && Boolean(this.banner.url);
    },
    hasBanner() {
      return this.isImage || this.isFramedHtml || Boolean(this.banner?.html);
    },
  },
};
</script>

<style scoped>
/* The visible strip: the ratio the artwork is drawn to, capped so an unusually
   wide column cannot turn a banner into a billboard, and floored so a phone
   still gets a band rather than a sliver. */
.category-banner-window {
  position: relative;
  width: 100%;
  aspect-ratio: 1196 / 210;
  min-height: 56px;
  max-height: 240px;
  overflow: hidden;
  border-radius: 4px;
}

/* Taller than any of these banner documents needs, so the document centres its
   own content inside the frame; centring the frame in the window above then
   puts that content in the middle of the strip and crops the document's outer
   padding evenly, instead of showing the top of it and cutting the artwork. */
.category-banner-frame {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 300px;
  transform: translateY(-50%);
  border: 0;
  display: block;
}
</style>
